import { cssVars } from '@/shared/ui';
import type { PortalLoadStatus } from '../api/types';

function remaining(seconds: number | null) {
  if (seconds === null) return null;
  if (seconds < 5) return 'almost done';
  if (seconds < 90) return `about ${String(Math.ceil(seconds / 5) * 5)} s left`;
  return `about ${String(Math.round(seconds / 60))} min left`;
}

/**
 * "Loading portal data" with the live percentage of the backend's data load, the running step and the time left.
 * Without a status (an older backend, or before the first answer) it is the plain spinner line.
 */
export function PortalLoadProgress({ status }: { status: PortalLoadStatus | undefined }) {
  const percent =
    status?.state === 'loaded' ? 100 : status?.state === 'loading' ? status.percent : null;
  const detail =
    status?.state === 'loading'
      ? [
          status.step &&
            `${status.step} (step ${String(status.stepNumber)} of ${String(status.stepCount)})`,
          remaining(status.remainingSeconds),
        ].filter(Boolean)
      : status?.state === 'loaded'
        ? ['Opening the page']
        : status?.state === 'failed'
          ? ['The last attempt failed; retrying']
          : [];

  return (
    <section className="load-progress" role="status" aria-live="polite">
      <p className="load-progress__title">
        <span className="spinner" aria-hidden="true" />
        Loading portal data
        {percent !== null && <span className="load-progress__percent">{percent}%</span>}
      </p>
      {percent !== null && (
        <span
          className="meter load-progress__bar"
          role="progressbar"
          aria-label="Portal data loaded"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <span style={cssVars({ w: `${String(percent)}%` })} />
        </span>
      )}
      {detail.length > 0 && <p className="load-progress__detail">{detail.join(' · ')}</p>}
    </section>
  );
}
